import { DataHeader, DataSourceMode, DropdownSelectionEvent, Label, Layout, TableRowDisplayEvent, TableSelectionEvent } from "@mcleod/components";
import { LogManager, ModelRow } from "@mcleod/core";
import { ImageName } from "@mcleod/images";
import { AutogenLayoutMobileCommFormGroupByTable } from "./autogen/AutogenLayoutMobileCommFormGroupByTable";
import { MobileCommForm } from "./MobileCommForm";
import { MobileCommFormGroupByVendor } from "./MobileCommFormGroupByVendor";

const log = LogManager.getLogger("lme.dispatch.mobilecomm.MobileCommFormGroupByTable");

// Constants for group by values
const GROUP_BY_VENDOR = "V";
const GROUP_BY_FORM = "F";

export class MobileCommFormGroupByTable extends AutogenLayoutMobileCommFormGroupByTable {

    async onLoad(): Promise<void> {
        if (this.textboxGroupBy.items.length > 0) {
            this.textboxGroupBy.setSelectedItemFromValue(this.textboxGroupBy.items[0].value);
        }
    }

    textboxGroupByAfterDropdownSelection(event: DropdownSelectionEvent): void {
        const groupByValue = event.selection.value;

        if (groupByValue === GROUP_BY_VENDOR) {
            this.handleVendorGrouping();
        } else if (groupByValue === GROUP_BY_FORM) {
            this.handleFormGrouping();
        }

        this.mainDataSource.search({ group_by: groupByValue });
    }

    /**
     * Configures the UI for vendor-based grouping.
     */
    private handleVendorGrouping(): void {
        this.setVendorColumnVisibility();
        this.setVendorLayoutVisibility(false);
        this.setIdLayoutVisibility(false);
        this.clearIdLayoutData();
    }

    /**
     * Configures the UI for form-based grouping.
     */
    private handleFormGrouping(): void {
        this.setFormColumnVisibility();
        this.setVendorLayoutVisibility(false);
        this.setIdLayoutVisibility(false);
        this.clearVendorLayoutData();
    }

    /**
     * Sets column visibility for vendor grouping mode.
     */
    private setVendorColumnVisibility(): void {
        this.tableFormGroupBy.visible = true;
        this.tableFormGroupByAll.visible = false;
    }

    /**
     * Sets column visibility for form grouping mode.
     */
    private setFormColumnVisibility(): void {
        this.tableFormGroupByAll.visible = true;
        this.tableFormGroupBy.visible = false;
    }

    /**
     * Sets the visibility of the vendor layout and optionally clears its data.
     */
    private setVendorLayoutVisibility(visible: boolean): void {
        const byVendorLayout = this.getVendorLayout();
        if (byVendorLayout) {
            byVendorLayout.visible = visible;
        }
    }

    private setIdLayoutVisibility(visible: boolean): void {
        const byIdLayout = this.getIdLayout();
        if (byIdLayout) {
            byIdLayout.visible = visible;
        }
    }

    /**
     * Clears the vendor layout data.
     */
    private clearVendorLayoutData(): void {
        const byVendorLayout = this.getVendorLayout();
        if (byVendorLayout) {
            byVendorLayout.mainDataSource.setRowsAndMode(DataSourceMode.UPDATE, []);
        }
    }

    private clearIdLayoutData(): void {
        const byIdLayout = this.getIdLayout();
        if (byIdLayout) {
            byIdLayout.mainDataSource.setRowsAndMode(DataSourceMode.UPDATE, []);
        }
    }

    /**
     * Gets the vendor layout component.
     */
    private getVendorLayout(): MobileCommFormGroupByVendor | null {
        return this.owner.layoutFormGroupByVendor as MobileCommFormGroupByVendor || null;
    }

    private getIdLayout(): MobileCommForm | null {
        return this.owner.layoutFormGroupById as MobileCommForm || null;
    }

    /**
     * Handles table row selection based on the current grouping mode.
     */
    async tableFormGroupByOnSelect(event: TableSelectionEvent): Promise<void> {
        const groupBy = this.textboxGroupBy.text;

        if (!event.newRow) {
            this.hideAllLayouts();
            return;
        }

        if ("Vendor" === groupBy) {
            await this.handleVendorSelection(event.newRow.data);
        } else if ("All Forms" === groupBy) {
            await this.handleFormSelection(event.newRow.data);
        }

        this.scrollToSelectedRow();
    }

    /**
     * Hides all layout components when no row is selected.
     */
    private hideAllLayouts(): void {
        const byIdLayout = this.getIdLayout();
        const byVendorLayout = this.getVendorLayout();

        if (byIdLayout) {
            byIdLayout.visible = false;
        }
        if (byVendorLayout) {
            byVendorLayout.visible = false;
        }
    }

    /**
     * Handles selection when grouping by vendor.
     */
    private async handleVendorSelection(modelRow: ModelRow): Promise<void> {
        const byVendorLayout = this.getVendorLayout();
        if (!byVendorLayout) {
            return;
        }

        byVendorLayout.visible = true;
        const vendorId = modelRow?.getString("form_vendor", null);
        const vendorName = modelRow?.getString("vendor_name", null);

        if (vendorId) {
            await byVendorLayout.mainDataSource.search({ mc_control_id: vendorId });
            byVendorLayout.labelVendorName.text = vendorName;
        }
    }

    /**
     * Handles selection when grouping by all forms.
     */
    private async handleFormSelection(modelRow: ModelRow): Promise<void> {
        const byIdLayout = this.getIdLayout();
        if (!byIdLayout) {
            return;
        }

        const selectedIndex = this.tableFormGroupByAll.selectedIndex;
        byIdLayout.visible = true;

        const formId = modelRow?.getString("id", null);
        const vendorId = modelRow?.getString("form_vendor", null);
        const formType = modelRow?.getString("form_type", null);

        if (formId) {
            await this.setupFormLayout(byIdLayout, formId, vendorId, formType, selectedIndex);
        }
    }

    /**
     * Sets up the form layout with data and event handlers.
     */
    private async setupFormLayout(
        byIdLayout: MobileCommForm,
        formId: string,
        vendorId: string,
        formType: string,
        selectedIndex: number
    ): Promise<void> {
        // Load data
        byIdLayout.mainDataSource.mode = DataSourceMode.UPDATE;
        await byIdLayout.mainDataSource.search({ id: formId, mc_control_id: vendorId, form_type: formType });

        // Restore selection state
        this.tableFormGroupByAll.selectedIndex = selectedIndex;
        this.tableFormGroupBy.selectedRow?.scrollIntoView();

        // Configure data header
        this.configureDataHeader(byIdLayout, selectedIndex);
    }

    /**
     * Configures the data header for the form layout.
     */
    private configureDataHeader(byIdLayout: MobileCommForm, selectedIndex: number): void {
        // Set up navigation and data source
        byIdLayout.dataHeader.navigateOnClose = async () => {
            await this.mainDataSource.search(this.mainDataSource.lastSearch);
            byIdLayout.visible = false;
        };

        byIdLayout.dataHeader.borderBottomWidth = 1;
        const saveButton = byIdLayout.dataHeader?.["saveButton"];
        if (saveButton) {
            saveButton.dataSource = byIdLayout.mainDataSource;
        }
        byIdLayout.dataHeader.dataSource = byIdLayout.mainDataSource;

        // Remove existing data header if present
        if (byIdLayout.components[0] instanceof DataHeader) {
            byIdLayout.components.shift();
        }

        // Set up execute handler
        byIdLayout.dataHeader.onExecute = async () => {
            byIdLayout.mainDataSource.post();
            await this.mainDataSource.search(this.mainDataSource.lastSearch);
            this.tableFormGroupByAll.selectedIndex = selectedIndex;
        };

        // Add data header to the beginning
        byIdLayout.components.unshift(byIdLayout.dataHeader);
        byIdLayout.relayout();
    }

    /**
     * Scrolls to the currently selected row if one exists.
     */
    private scrollToSelectedRow(): void {
        if (this.tableFormGroupByAll.selectedRow) {
            this.tableFormGroupByAll.selectedRow.scrollIntoView();
        }
    }

    tableFormGroupByAllOnRowDisplay(event: TableRowDisplayEvent): void {
        const tableRow = event.getTableRow();

        this.updateIsActiveLabel(tableRow);
        this.setupFormTitleTooltip(tableRow);
    }

    /**
     * Updates the active status label with the appropriate icon.
     */
    private updateIsActiveLabel(tableRow: any): void {
        const labelIsActive = tableRow.findComponentById("labelIsActive") as Label;
        if (!labelIsActive) {
            return;
        }

        let isActive: string | boolean;
        if (tableRow.data instanceof ModelRow) {
            isActive = (tableRow.data as ModelRow)?.getBoolean("is_active", null);
        } else {
            isActive = tableRow.data["is_active"];
        }
        labelIsActive.text = "";
        labelIsActive.imageName = (isActive || isActive === "Y") ? ImageName.circleCheck : ImageName.inactiveCircle;
    }

    /**
     * Sets up the tooltip for the form title label.
     */
    private setupFormTitleTooltip(tableRow: any): void {
        const formTitleLabel = tableRow.findComponentById("labelFormTitle") as Label;
        if (!formTitleLabel) {
            return;
        }

        const vendorId = tableRow.data?.get("form_vendor", null);
        const title = tableRow.data?.get("title", null);
        const formId = tableRow.data?.get("id", null);
        const formType = tableRow.data?.get("form_type", null);

        formTitleLabel.text = formId + " - " + title;

        formTitleLabel.tooltipDynamicContent = async () => {
            const layout = await Layout.getLoadedLayout("lme/dispatch/mobilecomm/MobileCommFormQuickInfo") as Layout;
            await layout.mainDataSource?.search({
                id: formId,
                mc_control_id: vendorId,
                form_type: formType
            });
            return layout;
        };
    }
}
